import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const AppName = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  color: #444;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;
    color: #444;
  }
`;

const ContactLink = styled.a`
  color: #007AFF;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TimeToGoPrivacy: React.FC = () => {
  return (
    <Container>
      <Title>Privacy Policy</Title>
      <AppName>TimeToGo: Avoid Traffic</AppName>
      <LastUpdated>Last updated: <time dateTime="2026-10-03">October 3, 2026</time></LastUpdated>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Paragraph>
          TimeToGo is a commute time monitoring app that helps you track travel times between locations.
          We are committed to protecting your privacy and being transparent about our data practices.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Data Collection</SectionTitle>
        <Paragraph>
          <strong>Your saved routes and addresses are stored locally on your device.</strong>
          {' '}TimeToGo does not operate a backend for storing your routes. Address searches and
          route requests go directly to Apple. Usage analytics go to Google/Firebase, as described below.
        </Paragraph>
        <Paragraph>
          Both the iPhone and Mac release versions use Firebase Analytics. The Mac app has no
          advertising; the iPhone app also uses Google AdMob. No TimeToGo account is required,
          but analytics uses identifiers, so this does not mean all collected data is anonymous.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Local Storage</SectionTitle>
        <Paragraph>
          The app uses your device's local storage (UserDefaults) to save:
        </Paragraph>
        <List>
          <li>Your saved routes (origin and destination locations)</li>
          <li>Your acceptable commute time preferences</li>
        </List>
        <Paragraph>
          Saved route data remains on your device. You can remove routes in the app. Deleting the app
          may not remove all local preferences or Mac Keychain trial records, and does not delete
          analytics data already sent to Google.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Third-Party Services</SectionTitle>
        <Paragraph>
          <strong>Apple MapKit:</strong> TimeToGo uses Apple's MapKit service for address suggestions,
          routes, traffic conditions, maps, and estimated travel times. Apple receives the search query
          or route endpoints required to provide those results. Apple associates Maps service data with
          random identifiers rather than your identity or Apple Account. These requests go directly from
          your device to Apple and are not routed through servers operated by the developer of this app.
          Please review{' '}
          <ContactLink href="https://www.apple.com/legal/privacy/data/en/apple-maps/" target="_blank" rel="noopener noreferrer">
            Apple Maps &amp; Privacy
          </ContactLink>{' '}
          for more information.
        </Paragraph>
        <Paragraph>
          <strong>Google AdMob (iPhone version only):</strong> We use Google AdMob to display advertisements in the iPhone app.
          AdMob may collect and process data for advertising purposes, including device identifiers
          and usage data. Please review{' '}
          <ContactLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google's Privacy Policy
          </ContactLink>{' '}
          for details on how they handle your data.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Firebase Analytics (iPhone and Mac)</SectionTitle>
        <Paragraph>
          We use Google Analytics for Firebase to understand feature usage, improve TimeToGo,
          and measure upgrades. Google receives and processes these events, and we can view
          analytics reports. Our custom analytics events do not include route names, addresses,
          search queries, or precise coordinates.
        </Paragraph>
        <List>
          <li><strong>Mac:</strong> Events record saving a route, starting monitoring, viewing the
            upgrade screen (including whether the trial has expired), and completing a purchase
            (yearly or lifetime plan).</li>
          <li><strong>iPhone:</strong> Events record route creation and swapping, monitoring,
            traffic status changes, opening the information page, and opening a navigation app.
            Parameters include the acceptable commute threshold and estimated travel time in minutes,
            traffic status, and the selected navigation app.</li>
        </List>
        <Paragraph>
          The SDK also automatically collects an app-instance identifier, app and device information
          (such as app version, device model, and operating system), and lifecycle and usage events
          such as sessions. It may measure in-app purchases and subscriptions through StoreKit.
          Google derives general location from masked IP addresses; this is separate from the
          precise location you optionally use for route calculations.
        </Paragraph>
        <Paragraph>
          Analytics is enabled in release builds, including TestFlight. It is disabled in Debug
          builds and in the Mac App Preview demo mode. For Google's data practices, see{' '}
          <ContactLink href="https://support.google.com/analytics/answer/10285841" target="_blank" rel="noopener noreferrer">
            Google Analytics data collection disclosures
          </ContactLink>{' '}and{' '}
          <ContactLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google's Privacy Policy
          </ContactLink>.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Privacy Choices and iPhone Advertising</SectionTitle>
        <Paragraph>
          The current app does not provide an in-app switch to disable Firebase Analytics.
          Denying location permission or advertising tracking permission does not turn off analytics.
        </Paragraph>
        <Paragraph>
          On iPhone, Google's consent form is shown when required for advertising. You can respond
          to that form and manage TimeToGo's tracking permission in iOS Settings under
          Privacy &amp; Security → Tracking. Tracking permission concerns advertising tracking;
          it is separate from app usage analytics. Reinstalling is not a guaranteed way to reset
          consent or delete data already collected by third-party services.
        </Paragraph>
        <Paragraph>
          For privacy questions or requests concerning your data, contact{' '}
          <ContactLink href="mailto:support@ai4u.co.nz">support@ai4u.co.nz</ContactLink>.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Location Data</SectionTitle>
        <Paragraph>
          Location access is optional. On iPhone, you can use your current location to set a route
          endpoint. On Mac, you can select your current location for an endpoint; the app may obtain
          your location when you set up that route or start monitoring it, including scheduled starts.
          The Mac app stops location updates after the location request finishes.
        </Paragraph>
        <Paragraph>
          Apple location and mapping services process the location to find an address and calculate
          the route. Precise coordinates are not included in our custom Firebase Analytics events
          or sent to a TimeToGo backend. You can enter fixed addresses without granting location
          permission, or change location permission in your device's Privacy &amp; Security settings.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Changes to This Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time. Any changes will be reflected on this page
          with an updated revision date.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this Privacy Policy, please contact us at:{' '}
          <ContactLink href="mailto:support@ai4u.co.nz">support@ai4u.co.nz</ContactLink>
        </Paragraph>
      </Section>
    </Container>
  );
};

export default TimeToGoPrivacy;
